import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Shield, Search, ChevronDown, ChevronUp, Check, X, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const API_BASE = window.location.origin;

const CATEGORY_LABELS = {
    core: 'Core Card Controls',
    card_advanced: 'Card Settings — Advanced',
    pages: 'Page Access',
    vouchers: 'Voucher System',
    admin: 'Administration',
};

const PERMISSION_LABELS = {
    review_shield_toggle: 'Toggle Review Shield ON/OFF',
    use_helper: 'Use Ask my helper',
    review_destination: 'Select Review Destination',
    assign_staff: 'Assign Staff to Cards',
    route_public_review: 'Route to Public Review',
    card_landing_page: 'Landing Page Settings',
    card_branding_logo: 'Branding & Logo',
    card_adverts: 'Advertisements',
    card_qr_config: 'QR Code Configuration',
    card_schedule: 'Schedule Settings',
    card_language: 'Language Settings',
    card_global_review: 'Global Review Settings',
    card_review_shield_advanced: 'Review Shield Advanced',
    card_notification_config: 'Notification Configuration',
    card_video_media: 'Video / Media',
    card_gift_card: 'Gift Card Settings',
    card_review_counter: 'Review Counter',
    view_employees: 'View Employees',
    manage_employees: 'Manage Employees',
    view_locations: 'View Locations',
    manage_locations: 'Manage Locations',
    view_analytics: 'View Analytics',
    view_roi_dashboard: 'ROI Dashboard',
    view_sales_report: 'Sales Report',
    view_shift_insights: 'Shift Insights',
    view_location_health: 'Location Health',
    view_executive_summary: 'Executive Summary',
    view_brand_comparison: 'Brand Comparison',
    view_url_registry: 'URL Registry',
    view_review_shield_results: 'Review Shield Results',
    manage_vouchers: 'Manage Vouchers',
    view_voucher_reports: 'Voucher Ledger / Reports',
    manage_organizations: 'Manage Organizations',
    manage_brands: 'Manage Brands',
    manage_notifications: 'Manage Notifications',
    manage_platform_values: 'Platform Values',
    manage_integrations: 'Integrations',
    manage_automation: 'Automation Rules',
    view_management_audit: 'Management Audit Dashboard',
};

// Insight Mode — the 7 high-consequence governance permissions whose
// individual rows carry a dedicated rail entry. Permissions NOT in this
// map render normally (no halo) so the rail stays focused on the
// authorities that actually warrant teaching.
const GOV_INSIGHT_KEY = {
    view_management_audit: 'permissions.workflow.toggle.view-management-audit',
    manage_vouchers: 'permissions.workflow.toggle.manage-vouchers',
    view_voucher_reports: 'permissions.workflow.toggle.view-voucher-reports',
    manage_organizations: 'permissions.workflow.toggle.manage-organizations',
    view_url_registry: 'permissions.workflow.toggle.view-url-registry',
    view_executive_summary: 'permissions.workflow.toggle.view-executive-summary',
    manage_employees: 'permissions.workflow.toggle.manage-employees',
};

export default function PermissionsManager() {
    const { t } = useTranslation();
    const { user, isSuperAdmin, isPartner } = useAuth();
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [permissions, setPermissions] = useState({});
    const [schema, setSchema] = useState(null);
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [expandedCats, setExpandedCats] = useState({});
    const token = localStorage.getItem('yulontap_token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const loadUsers = useCallback(async () => {
        const res = await fetch(`${API_BASE}/api/admin/users`, { headers });
        if (res.ok) {
            const data = await res.json();
            // Real super admins are never editable here. A PARTNER is a
            // super_admin record with partner_access, and this screen is exactly
            // how its access is granted — so partners stay in the list.
            setUsers(data.filter(u => u.role !== 'super_admin' || u.partner_access === true));
        }
    }, []);

    const loadSchema = useCallback(async () => {
        const res = await fetch(`${API_BASE}/api/permissions/schema`, { headers });
        if (res.ok) setSchema(await res.json());
    }, []);

    const loadUserPerms = useCallback(async (userId) => {
        const res = await fetch(`${API_BASE}/api/permissions/user/${userId}`, { headers });
        if (res.ok) {
            const data = await res.json();
            setPermissions(data.permissions);
        }
    }, []);

    useEffect(() => { loadUsers(); loadSchema(); }, [loadUsers, loadSchema]);

    useEffect(() => {
        if (selectedUserId) loadUserPerms(selectedUserId);
    }, [selectedUserId, loadUserPerms]);

    const togglePermission = (key) => {
        setPermissions(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const savePermissions = async () => {
        if (!selectedUserId) return;
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE}/api/permissions/user/${selectedUserId}`, {
                method: 'PUT', headers, body: JSON.stringify({ permissions }),
            });
            if (res.ok) toast.success('Permissions saved');
            else toast.error('Failed to save');
        } catch { toast.error('Error saving permissions'); }
        setSaving(false);
    };

    const resetToDefaults = async () => {
        if (!selectedUserId) return;
        const res = await fetch(`${API_BASE}/api/permissions/reset/${selectedUserId}`, {
            method: 'POST', headers,
        });
        if (res.ok) {
            const data = await res.json();
            setPermissions(data.permissions);
            toast.success('Permissions reset to defaults');
        }
    };

    const toggleCategory = (cat) => {
        setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));
    };

    const enableAllInCategory = (catPerms) => {
        setPermissions(prev => {
            const updated = { ...prev };
            catPerms.forEach(k => { updated[k] = true; });
            return updated;
        });
    };

    const disableAllInCategory = (catPerms) => {
        setPermissions(prev => {
            const updated = { ...prev };
            catPerms.forEach(k => { updated[k] = false; });
            return updated;
        });
    };

    // A partner never sees this screen at all — not even a refusal message.
    if (isPartner()) return <Navigate to="/cards" replace />;
    if (!isSuperAdmin()) return <div className="p-8 text-center text-muted-foreground">{t('permissionsManager.accessDenied')}</div>;

    const selectedUser = users.find(u => u.id === selectedUserId);
    const filteredUsers = users.filter(u =>
        (u.name || u.email || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="space-y-6" data-testid="permissions-manager">
            <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-red-500" />
                <div>
                    <h1 className="text-2xl font-bold">{t('permissionsManager.title')}</h1>
                    <p className="text-sm text-muted-foreground">Control feature visibility per user. Hidden features are completely invisible.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* User List */}
                <Card className="lg:col-span-1" data-insight-id="permissions.user-list" data-insight-label="User list">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm">{t('permissionsManager.selectUser')}</CardTitle>
                        <Input
                            placeholder="Search users..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="mt-2"
                            data-testid="perm-user-search"
                        />
                    </CardHeader>
                    <CardContent className="max-h-[500px] overflow-y-auto space-y-1">
                        {filteredUsers.map((u, idx) => (
                            <button
                                key={u.id}
                                onClick={() => setSelectedUserId(u.id)}
                                data-testid={`perm-user-${u.id}`}
                                data-insight-id={idx === 0 ? "permissions.workflow.user-row" : undefined}
                                data-insight-label={idx === 0 ? "Operator authority row" : undefined}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                    selectedUserId === u.id
                                        ? 'bg-primary/10 text-primary border border-primary/30'
                                        : 'hover:bg-muted'
                                }`}
                            >
                                <p className="font-medium truncate">{u.name || u.email}</p>
                                <p className="text-xs text-muted-foreground">{u.role}</p>
                            </button>
                        ))}
                        {filteredUsers.length === 0 && (
                            <p className="text-xs text-muted-foreground text-center py-4">{t('permissionsManager.noUsersFound')}</p>
                        )}
                    </CardContent>
                </Card>

                {/* Permission Editor */}
                <Card className="lg:col-span-2" data-insight-id="permissions.editor" data-insight-label="Permission editor">
                    <CardHeader className="pb-3 flex flex-row items-center justify-between">
                        <div>
                            <CardTitle className="text-sm">
                                {selectedUser ? `${selectedUser.name || selectedUser.email} — ${selectedUser.role}` : 'Select a user'}
                            </CardTitle>
                        </div>
                        {selectedUser && (
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={resetToDefaults} data-testid="perm-reset-btn" data-insight-id="permissions.action.reset" data-insight-label="Reset to defaults">
                                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
                                </Button>
                                <Button size="sm" onClick={savePermissions} disabled={saving} data-testid="perm-save-btn" data-insight-id="permissions.action.save" data-insight-label="Save permissions">
                                    {saving ? 'Saving...' : 'Save Permissions'}
                                </Button>
                            </div>
                        )}
                    </CardHeader>
                    <CardContent className="max-h-[500px] overflow-y-auto">
                        {!selectedUser ? (
                            <p className="text-sm text-muted-foreground text-center py-8">Select a user from the list to manage their permissions.</p>
                        ) : schema ? (
                            <div className="space-y-2">
                                {Object.entries(schema.categories).map(([catKey, cat], catIdx) => {
                                    const isExpanded = expandedCats[catKey] !== false;
                                    const onCount = cat.permissions.filter(k => permissions[k]).length;
                                    return (
                                        <div key={catKey} className="border rounded-lg" data-testid={`perm-category-${catKey}`}>
                                            <button
                                                onClick={() => toggleCategory(catKey)}
                                                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-muted/50 transition-colors"
                                                data-insight-id={`permissions.workflow.category-${catKey}`}
                                                data-insight-label={`${CATEGORY_LABELS[catKey] || cat.label} authority class`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-medium">{CATEGORY_LABELS[catKey] || t(`permissionsManager.cat.${catKey}`, cat.label)}</span>
                                                    <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{onCount}/{cat.permissions.length}</span>
                                                </div>
                                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                            </button>
                                            {isExpanded && (
                                                <div className="px-4 pb-3 space-y-2 border-t pt-2">
                                                    <div className="flex gap-2 mb-2">
                                                        <button onClick={() => enableAllInCategory(cat.permissions)} className="text-xs text-emerald-600 hover:underline flex items-center gap-1"
                                                            data-insight-id={catIdx === 0 ? "permissions.workflow.enable-all" : undefined}
                                                            data-insight-label={catIdx === 0 ? "Enable every authority in this class" : undefined}
                                                        >
                                                            <Check className="w-3 h-3" /> Enable All
                                                        </button>
                                                        <button onClick={() => disableAllInCategory(cat.permissions)} className="text-xs text-red-600 hover:underline flex items-center gap-1"
                                                            data-insight-id={catIdx === 0 ? "permissions.workflow.disable-all" : undefined}
                                                            data-insight-label={catIdx === 0 ? "Revoke every authority in this class" : undefined}
                                                        >
                                                            <X className="w-3 h-3" /> Disable All
                                                        </button>
                                                    </div>
                                                    {cat.permissions.map(permKey => {
                                                        const gov = GOV_INSIGHT_KEY[permKey];
                                                        return (
                                                            <div key={permKey} className="flex items-center justify-between py-1" data-testid={`perm-toggle-${permKey}`}
                                                                data-insight-id={gov}
                                                                data-insight-label={gov ? `Authority: ${PERMISSION_LABELS[permKey] || permKey}` : undefined}
                                                            >
                                                                <span className="pr-3">
                                                                    <span className="block text-sm">
                                                                        {PERMISSION_LABELS[permKey]
                                                                            || t(`permissionsManager.perm.${permKey}.label`, permKey)}
                                                                    </span>
                                                                    {t(`permissionsManager.perm.${permKey}.hint`, '') && (
                                                                        <span className="block text-xs text-muted-foreground"
                                                                              data-testid={`perm-hint-${permKey}`}>
                                                                            {t(`permissionsManager.perm.${permKey}.hint`)}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                                <Switch
                                                                    checked={permissions[permKey] || false}
                                                                    onCheckedChange={() => togglePermission(permKey)}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
